import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Undo } from '@mui/icons-material';

import ToolbarButton from '../ToolbarButton';
import { EActiveDrawer, ESubMenu, EToolbarButton } from '../../../../@types/Enums';
import HistoryModal from '../modals/HistoryModal';

interface HistoryButtonProps {
  activeDrawer: EActiveDrawer;
  activeToolbarButton: EToolbarButton | null;
  sizes: any;
  handleToolbarButtonPress: (buttonId: EToolbarButton, subMenuType?: ESubMenu, activatedDrawer?: EActiveDrawer) => void;
  setActiveToolbarButton: (button: EToolbarButton | null) => void;
  onPositionUpdate: (rect: DOMRect) => void;
}

const HistoryButton = forwardRef<HTMLButtonElement, HistoryButtonProps>(
  ({ activeDrawer, activeToolbarButton, sizes, handleToolbarButtonPress, setActiveToolbarButton, onPositionUpdate }, ref) => {
    const intl = useIntl();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [modalOpen, setModalOpen] = useState(false);
    useImperativeHandle(ref, () => buttonRef.current!);

    useEffect(() => {
      if (activeToolbarButton !== EToolbarButton.HISTORY) {
        setModalOpen(false);
      }
    }, [activeToolbarButton]);

    // Update position if needed when active.
    useEffect(() => {
      if (activeToolbarButton === EToolbarButton.HISTORY && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        onPositionUpdate(rect);
      }
    }, [activeToolbarButton, onPositionUpdate]);

    const handleClick = () => {
      if (!modalOpen) {
        setModalOpen(true);
        if (activeToolbarButton !== EToolbarButton.HISTORY) {
          setActiveToolbarButton(EToolbarButton.HISTORY);
          handleToolbarButtonPress(EToolbarButton.HISTORY);
        }
      } else {
        setModalOpen(false);
      }
    };

    return (
      <>
        <ToolbarButton
          ref={buttonRef}
          tooltip={intl.formatMessage({ id: 'Toolbar.history' })}
          onClick={handleClick}
          icon={<Undo />}
          active={modalOpen || activeDrawer === EActiveDrawer.HISTORY}
          sizes={sizes}
        />

        <HistoryModal open={modalOpen} onClose={() => setModalOpen(false)} buttonPosition={buttonRef.current?.getBoundingClientRect()} />
      </>
    );
  },
);

export default HistoryButton;
